// Bandeja -> Correos: las conversaciones de los buzones del CRM. Lista de hilos a la izquierda, el hilo abierto a la
// derecha con la respuesta abajo. En el celular se ve una cosa a la vez.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { guardarRespuestaEnCaso } from '../../api/casos';
import { empezarSincronizacion, enviar, mensajesDeHilo, subirAdjunto } from '../../api/correo';
import { type CambioEstado, type EstadoCorreo, estadosDeHilos, guardarEstado, misEstados } from '../../api/correoEstado';
import { useSession } from '../../auth/session';
import { useBorradores } from '../../lib/borradores';
import { aplicarReglas, useReglas } from '../../lib/reglas';
import { type EstadoHilo, filtrarVista, noLeido, type Vista, asuntoDeCaso, asuntoReenvio, asuntoRespuesta, contraparte, remitente, sinCitas, textoReenvio } from '../../lib/correo';
import { ago } from '../../lib/format';
import { Icon } from '../../lib/icons';
import { Avatar, Empty, ErrorBox, Skeleton } from '../../ui/bits';
import { BuzonDialog } from '../../ui/Buzones';
import { useBuzonesVisibles, useHilos } from './useHilos';
import { useFirma } from '../../ui/Plantillas';
import { abrirDialogo } from '../../ui/forms';
import { toast } from '../../ui/toasts';
import { Conversacion, juntarPorFecha, notasComoMensajes } from '../../ui/Conversacion';
import { type Eleccion, type EnvioRespuesta, Respuesta } from '../../ui/Respuesta';
import { CasoDeCorreo } from './CasoDeCorreo';
import { useCorreoDelCaso } from './useCorreoDelCaso';
import { conversacionDeCaso, msgDeCorreo, msgDeGuardado } from '../../lib/conversacionCaso';
import { plegar, volar } from '../../lib/vuelo';
import { conCopiaAlGrupo, esReenvioDeCompanero } from '../../lib/casoDeHilo';
import { AvisoCasoEnCorreo, useCasoEnCorreo } from '../../ui/AvisoCasoEnCorreo';
import { BandejaDespejada } from '../../ui/BandejaDespejada';

const nombreDe = (p?: { displayName?: string | null; handle: string }) => (p ? p.displayName?.trim() || p.handle : 'Desconocido');

const VISTAS: [Vista, string][] = [
  ['entrada', 'Entrada'],
  ['noleidos', 'No leídos'],
  ['destacados', 'Destacados'],
  ['enviados', 'Enviados'],
  ['archivados', 'Archivados'],
];

// Leido, destacado y archivado por persona (objeto estadoCorreo). Los cambios se ven al instante y se guardan detras.
// Con `soloHilos` (contadores del menu y la campana) no se pide la lista completa de la persona (miles de filas, 25
// paginas seguidas): solo los estados de esos hilos. Si la pantalla de Correos ya tiene la lista completa reciente, se
// usa esa y no se pide nada.
type MapaEstados = Map<string, EstadoCorreo>;
export function useEstadosCorreo(soloHilos?: string[]) {
  const { yo } = useSession();
  const qc = useQueryClient();
  const ligero = !!soloHilos;
  const r = useQuery({ queryKey: ['correo-estados', yo.miembroId], queryFn: () => misEstados(yo.miembroId!), enabled: !!yo.miembroId && !ligero, staleTime: 60_000 });
  const usarCompleto = ligero && !!r.data && Date.now() - r.dataUpdatedAt < 3 * 60_000;
  const ids = useMemo(() => [...new Set(soloHilos ?? [])].sort().join(','), [soloHilos]);
  const idsRef = useRef(ids);
  idsRef.current = ids;
  const parcial = useQuery({
    queryKey: ['estados-hilos', yo.miembroId],
    queryFn: () => estadosDeHilos(yo.miembroId!, idsRef.current.split(',').filter(Boolean)),
    enabled: ligero && !!yo.miembroId && !!ids && !usarCompleto,
    staleTime: 60_000,
  });
  // Llegaron hilos nuevos a la entrada: se piden de nuevo los estados (una clave fija, para guardarla en el navegador).
  const idsPrevios = useRef(ids);
  useEffect(() => {
    if (!ligero || idsPrevios.current === ids) return;
    idsPrevios.current = ids;
    void qc.invalidateQueries({ queryKey: ['estados-hilos', yo.miembroId], exact: true }, { cancelRefetch: false });
  }, [ligero, ids, qc, yo.miembroId]);
  const cambiar = useCallback(
    async (hiloId: string, cambios: CambioEstado) => {
      if (!yo.miembroId) return;
      const mapa = qc.getQueryData<MapaEstados>(['correo-estados', yo.miembroId]) ?? new Map();
      const actual = mapa.get(hiloId) ?? qc.getQueryData<MapaEstados>(['estados-hilos', yo.miembroId])?.get(hiloId);
      const nuevoEstado = { id: actual?.id ?? '', hiloId, leidoEn: null, destacado: false, archivado: false, ...actual, ...cambios };
      // Solo se toca la lista completa si ya existe (con datos a medias, los contadores la tomarian por buena).
      if (qc.getQueryData(['correo-estados', yo.miembroId])) qc.setQueryData(['correo-estados', yo.miembroId], new Map(mapa).set(hiloId, nuevoEstado));
      qc.setQueryData<MapaEstados>(['estados-hilos', yo.miembroId], (m) => (m ? new Map(m).set(hiloId, nuevoEstado) : m));
      try {
        await guardarEstado(yo.miembroId, hiloId, actual?.id ? actual : undefined, cambios);
      } catch (e) {
        toast((e as Error).message, 'error');
      }
      void qc.invalidateQueries({ queryKey: ['correo-estados', yo.miembroId] });
      void qc.invalidateQueries({ queryKey: ['estados-hilos', yo.miembroId] });
    },
    [qc, yo.miembroId],
  );
  const datos = ligero && !usarCompleto ? parcial.data : r.data;
  return { estados: datos ?? (new Map() as MapaEstados), cambiar };
}

export function Correos() {
  const [sp, setSp] = useSearchParams();
  const hiloId = sp.get('hilo');
  const vista = (VISTAS.find(([v]) => v === sp.get('vista'))?.[0] ?? 'entrada') as Vista;
  const { estados: guardados, cambiar } = useEstadosCorreo();
  const { yo, cargandoYo } = useSession();
  const b = useBuzonesVisibles();
  const grupos = b.grupos;
  const [buzonSel, setBuzonSel] = useState('');
  const [q, setQ] = useState('');
  // La busqueda se hace en el servidor (todo el historial del buzon), con una pausa corta mientras se escribe.
  const [qServidor, setQServidor] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQServidor(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);
  const [conectar, setConectar] = useState(false);
  const qc = useQueryClient();
  const mios = useMemo(() => (b.data ?? []).map((x) => x.correo.toLowerCase()), [b.data]);
  const pendientes = (b.data ?? []).filter((x) => x.canal?.etapa === 'PENDING_CONFIGURATION');
  const [activando, setActivando] = useState(false);
  const activar = async () => {
    setActivando(true);
    try {
      for (const x of pendientes) await empezarSincronizacion(x.id);
      void qc.invalidateQueries({ queryKey: ['buzones'] });
      toast('Sincronizando: los correos empiezan a llegar en unos minutos.');
    } catch (e) {
      toast((e as Error).message, 'error');
    } finally {
      setActivando(false);
    }
  };
  // Cada buzon trae sus conversaciones completas del servidor, por paginas (antes: los ultimos 200 correos de todos).
  const cuentas = useMemo(() => (b.data ?? []).filter((x) => !buzonSel || x.correo.toLowerCase() === buzonSel.toLowerCase()).map((x) => x.id), [b.data, buzonSel]);
  // Entrada: todo menos el historial archivado. Archivados: lo que cada quien archivo (esta en la entrada) mas el
  // historial de Zammad, que se pide aparte para no tener que pasar paginas y paginas de correo reciente.
  const entrada = useHilos(cuentas, 'entrada', qServidor);
  const historial = useHilos(vista === 'archivados' ? cuentas : [], 'archivados', qServidor);
  const r = vista === 'archivados' ? historial : entrada;
  const todos = useMemo(
    () =>
      vista === 'archivados'
        ? [...entrada.hilos, ...historial.hilos.filter((h) => !entrada.hilos.some((x) => x.id === h.id))].sort((a, b) => b.ultimo.receivedAt.localeCompare(a.ultimo.receivedAt))
        : entrada.hilos,
    [vista, entrada.hilos, historial.hilos],
  );
  const reglas = useReglas();
  const estados = useMemo(() => aplicarReglas(todos, conHistorial(todos, guardados), reglas), [todos, guardados, reglas]);
  const cuentaNoLeidos = useMemo(() => filtrarVista(todos, estados, mios, 'noleidos').length, [todos, estados, mios]);
  const hilos = useMemo(() => filtrarVista(todos, estados, mios, vista), [todos, estados, mios, vista]);
  // Un hilo del historial conserva su archivado al abrirlo o destacarlo (el primer cambio crea su estado guardado).
  const historialSinEstado = (id: string) => !guardados.has(id) && !!todos.find((h) => h.id === id)?.archivadoPorDefecto;
  const cambiarHilo = useCallback(
    (id: string, c: CambioEstado) => cambiar(id, c.archivado === undefined && historialSinEstado(id) ? { ...c, archivado: true } : c),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cambiar, guardados, todos],
  );
  // Archivar desde la Entrada: un punto vuela de la fila a "Archivados" y la fila se pliega antes de salir.
  const [despejo, setDespejo] = useState(false);
  const cambiarConVuelo = useCallback(
    async (id: string, c: CambioEstado) => {
      if (c.archivado === true && vista === 'entrada') {
        const fila = document.querySelector<HTMLElement>(`.cl-item[data-hilo="${CSS.escape(id)}"]`);
        if (fila) {
          void volar(fila, document.querySelector('.cl-vistas [data-vista="archivados"]'));
          await plegar(fila);
        }
        setDespejo(true);
      }
      return cambiarHilo(id, c);
    },
    [cambiarHilo, vista],
  );
  const setVista = (v: Vista) => {
    const n = new URLSearchParams(sp);
    if (v === 'entrada') n.delete('vista');
    else n.set('vista', v);
    setSp(n, { replace: true });
  };

  const abrir = (id: string | null) => {
    const n = new URLSearchParams(sp);
    if (id) n.set('hilo', id);
    else n.delete('hilo');
    setSp(n, { replace: !!hiloId });
  };

  if (b.isPending || cargandoYo || grupos.isPending)
    return (
      <div className="view">
        <Skeleton rows={8} />
      </div>
    );
  if (b.isError)
    return (
      <div className="view">
        <ErrorBox error={b.error} onRetry={() => b.refetch()} />
      </div>
    );
  if (!b.data.length)
    return (
      <div className="view">
        <header className="vhead">
          <div>
            <h1>Correos</h1>
            <p className="sub">Envía y recibe correo sin salir del CRM</p>
          </div>
        </header>
        <Empty icon="mail" titulo="Conecta tu correo" texto="Con tu dirección y su contraseña, tus correos llegan aquí y a la ficha de cada contacto, y respondes desde el CRM.">
          <button className="btn primary" onClick={() => setConectar(true)}>
            <Icon name="mail" /> Conectar mi correo
          </button>
        </Empty>
        {conectar ? <BuzonDialog correoInicial={yo.email} onClose={() => setConectar(false)} /> : null}
      </div>
    );

  return (
    <div className={`correo ${hiloId ? 'con-hilo' : ''}`}>
      <section className="correo-lista" aria-label="Conversaciones">
        <div className="cl-h">
          <h1>Correos</h1>
          <button className="btn sm ghost" onClick={() => abrirDialogo({ tipo: 'reglasCorreo' })} title="Archivar o destacar correos solos">
            Reglas
          </button>
          <button className="btn sm primary" onClick={() => abrirDialogo({ tipo: 'correo' })}>
            <Icon name="edit" /> Redactar
          </button>
        </div>
        <nav className="cl-vistas" aria-label="Vistas">
          {VISTAS.map(([v, t]) => (
            <button key={v} aria-pressed={vista === v} onClick={() => setVista(v)} data-vista={v}>
              {t}
              {v === 'noleidos' && cuentaNoLeidos ? <span className="cnt">{cuentaNoLeidos}</span> : null}
            </button>
          ))}
        </nav>
        {(b.data ?? []).length > 1 ? (
          <select className="inp cl-buzon" value={buzonSel} onChange={(e) => setBuzonSel(e.target.value)} aria-label="Buzón">
            <option value="">Todos mis buzones</option>
            {(b.data ?? []).map((x) => {
              const g = (grupos.data ?? []).find((y) => y.cuentaId === x.id);
              return (
                <option key={x.id} value={x.correo}>
                  {g ? `${g.nombre} · ${x.correo}` : x.correo}
                </option>
              );
            })}
          </select>
        ) : null}
        <label className="field" style={{ margin: '0 12px 8px' }}>
          <Icon name="search" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por persona, asunto o texto" aria-label="Buscar correos" />
        </label>
        <Borradores />
        {pendientes.length ? (
          <div className="cl-aviso">
            <span>
              {pendientes.map((x) => x.correo).join(', ')} {pendientes.length === 1 ? 'está conectado' : 'están conectados'}, pero falta empezar a sincronizar.
            </span>
            <button className="btn sm primary" onClick={activar} disabled={activando}>
              {activando ? 'Activando…' : 'Empezar'}
            </button>
          </div>
        ) : null}
        {r.error ? <ErrorBox error={r.error} onRetry={() => r.refetch()} /> : null}
        {r.isLoading ? (
          <div style={{ padding: 12 }}>
            <Skeleton rows={8} />
          </div>
        ) : hilos.length || r.hasNextPage ? (
          <div className="cl-items">
            {hilos.map((h) => {
              const otro = contraparte(h.ultimo, mios);
              const salio = mios.includes((remitente(h.ultimo)?.handle ?? '').toLowerCase());
              const e = estados.get(h.id);
              const nuevo = noLeido(h, e, mios);
              return (
                <button key={h.id} className={`cl-item ${nuevo ? 'nuevo' : ''}`} aria-current={h.id === hiloId ? 'true' : undefined} onClick={() => abrir(h.id)} data-hilo={h.id}>
                  <Avatar nombre={nombreDe(otro)} size="sm" />
                  <span className="cl-t">
                    <span className="cl-top">
                      <b>{nombreDe(otro)}</b>
                      {h.cantidad > 1 ? <span className="cl-n">{h.cantidad}</span> : null}
                      {e?.destacado ? (
                        <span className="cl-star" aria-label="Destacado">
                          <Icon name="star" size={13} />
                        </span>
                      ) : null}
                      <time>{ago(h.ultimo.receivedAt)}</time>
                    </span>
                    <span className="cl-as">{h.ultimo.asunto}</span>
                    <span className="cl-pv">
                      {salio ? 'Tú: ' : ''}
                      {sinCitas(h.ultimo.texto).slice(0, 140) || '(sin texto)'}
                    </span>
                  </span>
                </button>
              );
            })}
            {r.hasNextPage ? (
              <button className="btn sm ghost cl-mas" onClick={() => void r.fetchNextPage()} disabled={r.isFetchingNextPage}>
                {r.isFetchingNextPage ? 'Cargando…' : 'Cargar correos más antiguos'}
              </button>
            ) : null}
          </div>
        ) : !q && (vista === 'noleidos' || (vista === 'entrada' && despejo)) ? (
          <BandejaDespejada texto={vista === 'noleidos' ? 'No te queda nada por leer.' : 'No te queda nada por responder.'} />
        ) : (
          <Empty
            icon="mail"
            titulo={q ? 'Nada con esa búsqueda' : vista === 'entrada' ? 'Aún no hay correos' : 'Nada en esta vista'}
            texto={q || vista !== 'entrada' ? undefined : 'Los correos de tu buzón aparecen aquí unos minutos después de conectarlo.'}
          />
        )}
      </section>
      <section className="correo-hilo" aria-label="Conversación">
        {hiloId ? (
          <Hilo id={hiloId} mios={mios} estado={estados.get(hiloId)} cambiar={cambiarConVuelo} historial={!!todos.find((h) => h.id === hiloId)?.archivadoPorDefecto} onVolver={() => abrir(null)} />
        ) : (
          <Empty icon="mail" titulo="Elige una conversación" texto="O escribe un correo nuevo con Redactar." />
        )}
      </section>
    </div>
  );
}

// Historial traido de Zammad sin caso abierto: se ve como archivado y leido mientras nadie lo devuelva a la entrada.
export function conHistorial<E extends EstadoHilo>(hilos: { id: string; archivadoPorDefecto?: boolean; ultimo: { receivedAt: string } }[], guardados: Map<string, E>) {
  if (!hilos.some((h) => h.archivadoPorDefecto && !guardados.has(h.id))) return guardados as Map<string, EstadoHilo>;
  const m = new Map<string, EstadoHilo>(guardados);
  for (const h of hilos) if (h.archivadoPorDefecto && !m.has(h.id)) m.set(h.id, { leidoEn: h.ultimo.receivedAt, destacado: false, archivado: true });
  return m;
}

export function Hilo({
  id,
  mios,
  estado,
  cambiar,
  historial = false,
  enCaso = false,
  desdeCuentaId,
  notas,
  onNota,
  onVolver,
  caso,
  unidos = [],
  casoId,
}: {
  // En un caso: los hilos unidos (D50) y sus mensajes guardados van en la misma conversacion (lib/conversacionCaso).
  unidos?: string[];
  casoId?: string | null;
  // En un caso: su numero va en el asunto ([Ticket#N]) para que la respuesta del cliente vuelva a el.
  caso?: { numero: number | null; titulo: string };
  id: string;
  mios: string[];
  estado?: { leidoEn: string | null; destacado: boolean; archivado: boolean };
  cambiar: (hiloId: string, c: CambioEstado) => Promise<void>;
  historial?: boolean;
  enCaso?: boolean;
  // En un caso se responde desde el buzon de su grupo (si la persona lo puede usar).
  desdeCuentaId?: string | null;
  // En un caso: sus notas internas van en la conversacion y "Nota" las agrega (D41).
  notas?: { autor: string; fecha: string; texto: string }[];
  onNota?: (texto: string) => Promise<void>;
  onVolver: () => void;
}) {
  const qc = useQueryClient();
  // Se responde desde un buzon tuyo o de tus grupos (el administrador, cualquiera).
  const b = useBuzonesVisibles();
  const r = useQuery({ queryKey: ['correos', 'hilo', id], queryFn: () => mensajesDeHilo(id), refetchInterval: 60_000 });
  const [sel, setSel] = useState<Eleccion>({ base: null, modo: 'responder' });
  const ultimaFecha = r.data?.[r.data.length - 1]?.receivedAt;
  // Abrir el hilo lo marca como leido hasta su ultimo mensaje.
  useEffect(() => {
    if (ultimaFecha && (!estado?.leidoEn || estado.leidoEn < ultimaFecha)) void cambiar(id, { leidoEn: ultimaFecha });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, ultimaFecha]);
  // Capa 1: si este correo es de un caso abierto, se ofrece responder desde el caso (buzon del grupo y [Ticket#N]).
  const participantes = useMemo(() => [...new Set((r.data ?? []).flatMap((m) => m.participantes.map((p) => p.handle.toLowerCase())))], [r.data]);
  const enCorreo = useCasoEnCorreo({ hiloId: id, asunto: r.data?.[0]?.asunto ?? '', participantes, activo: !enCaso && !!r.data?.length });
  const [desdeCaso, setDesdeCaso] = useState(false);
  const [reasunto, setReasunto] = useState(0);
  useEffect(() => setDesdeCaso(false), [id]);
  const casoElegido = desdeCaso && enCorreo.cuentaGrupo ? enCorreo.coincide?.caso : undefined;
  // Se responde desde el buzon que participa en el hilo, con su firma.
  const buzon = useMemo(
    () =>
      (casoElegido ? (enCorreo.cuentaGrupo ?? undefined) : undefined) ??
      (desdeCuentaId ? (b.data ?? []).find((x) => x.id === desdeCuentaId) : undefined) ??
      (b.data ?? []).find((x) => (r.data ?? []).some((m) => m.participantes.some((p) => p.handle.toLowerCase() === x.correo.toLowerCase()))) ??
      b.data?.[0],
    [b.data, r.data, desdeCuentaId, casoElegido, enCorreo.cuentaGrupo],
  );
  // Si igual sale desde un buzon personal una respuesta de un caso, copia oculta al buzon del grupo (D60): llega al caso.
  // En un caso abierto en pantalla, cuando no se puede usar el buzon del grupo y se cae al personal.
  const correoGrupoCaso = enCaso ? (b.grupos.data ?? []).find((g) => g.cuentaId && g.cuentaId === desdeCuentaId)?.correo : enCorreo.grupo?.correo;
  const ccoGrupo = buzon && enCorreo.esPersonal(buzon) && (enCaso || enCorreo.coincide) && correoGrupoCaso && correoGrupoCaso.toLowerCase() !== buzon.correo.toLowerCase() ? correoGrupoCaso : null;
  // En un caso, ademas de este hilo: los hilos unidos y los mensajes guardados del caso (p. ej. los de un caso de
  // Zammad que se unio a este). Fuera de un caso, solo este hilo.
  const extra = useCorreoDelCaso(enCaso ? unidos.filter((h) => h !== id) : [], enCaso ? casoId : null, enCaso ? casoId : null);
  const todosCorreos = useMemo(() => [...(r.data ?? []), ...extra.correo], [r.data, extra.correo]);
  const conv = useMemo(
    () =>
      enCaso
        ? conversacionDeCaso({ origen: 'CORREO', hiloId: id, correo: todosCorreos, guardados: extra.guardados.map((g) => msgDeGuardado(g)), mios, ocultos: extra.ocultos })
        : (r.data ?? []).map((m) => msgDeCorreo(m, mios)),
    [enCaso, id, todosCorreos, extra.guardados, extra.ocultos, r.data, mios],
  );
  // Los hilos unidos tambien quedan leidos al verlos en el caso.
  const ultimosUnidos = extra.porHilo.map((h) => `${h.id}:${h.ultimo ?? ''}`).join('|');
  useEffect(() => {
    for (const h of extra.porHilo) if (h.ultimo) void cambiar(h.id, { leidoEn: h.ultimo });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ultimosUnidos]);
  const firma = useFirma(buzon?.correo);

  if (r.isLoading) return <Skeleton rows={8} />;
  if (r.error) return <ErrorBox error={r.error} onRetry={() => r.refetch()} />;
  // En un caso, si su hilo no se puede leer pero los unidos si, se ve lo que hay.
  const ms = r.data?.length ? r.data : enCaso ? [...todosCorreos].sort((a, b) => a.receivedAt.localeCompare(b.receivedAt)) : [];
  // Vacio casi siempre es privacidad (D38): el correo solo llego a un buzon personal de otra persona, y ese buzon solo
  // lo lee su dueno (ni el administrador). Se dice claro en vez de "no disponible".
  if (!ms.length && !conv.length)
    return (
      <Empty
        icon="lock"
        titulo={enCaso ? 'El correo de este caso está en un buzón personal' : 'Conversación no disponible'}
        texto={
          enCaso
            ? 'Llegó solo al buzón personal de alguien del equipo, y un buzón personal solo lo lee su dueño (ni el administrador). Pídele que lo reenvíe a un buzón de grupo, como helpdesk@, o que te lo comparta.'
            : 'Este correo está en un buzón que no puedes leer, o ya no existe.'
        }
      />
    );
  const ultimo = ms[ms.length - 1] as (typeof ms)[number] | undefined;
  const otro = ultimo ? contraparte(ultimo, mios) : undefined;
  const asunto0 = ms[0]?.asunto ?? conv.find((m) => !m.interno && !m.oculto)?.asunto ?? '';
  // En un caso se cuentan todos sus mensajes (hilos unidos y guardados), sin las notas.
  const cuantos = enCaso ? conv.filter((m) => !m.interno).length : ms.length;
  // Se responde a un mensaje que se puede leer (nunca a la linea de un correo oculto).
  const legibles = conv.filter((m) => !m.oculto);
  const persona = ms.flatMap((m) => m.participantes).find((p) => p.personId && !mios.includes(p.handle.toLowerCase()));
  // Sin elegir, se responde al ultimo mensaje.
  const eleccion: Eleccion = { base: sel.base ?? legibles[legibles.length - 1] ?? null, modo: sel.modo, abrir: sel.abrir };

  const responder = async (e: EnvioRespuesta) => {
    if (e.modo === 'nota') {
      await onNota?.(e.texto.trim());
      return;
    }
    if (!buzon) throw new Error('No tienes un buzón desde el cual responder.');
    const original = e.base ? todosCorreos.find((m) => m.id === e.base!.id) : ultimo;
    const reenvio = e.modo === 'reenviar';
    const adjuntos = await Promise.all(e.adjuntos.map(subirAdjunto));
    await enviar({
      cuentaId: buzon.id,
      para: e.para,
      cc: e.cc,
      cco: ccoGrupo && !reenvio ? conCopiaAlGrupo(e, ccoGrupo) : e.cco,
      asunto: e.asunto,
      texto: reenvio && original ? `${e.texto}${textoReenvio(original)}` : e.texto,
      firma,
      responderA: reenvio ? undefined : (original?.headerMessageId ?? undefined),
      adjuntos,
    });
    // En un caso queda enseguida en su conversacion: el enviado llega al hilo cuando el buzon se sincroniza y, si se
    // respondio a un correo copiado de un buzon personal, en un hilo nuevo que el caso une despues (caso
    // #20261027900003, "respondi y no lo veo"). Cuando llega, conversacionDeCaso no lo repite (mismo remitente y hora).
    if (enCaso && casoId) {
      const nombres = e.adjuntos.map((a) => a.name).filter(Boolean);
      await guardarRespuestaEnCaso(casoId, {
        asunto: e.asunto,
        de: buzon.correo,
        para: e.para,
        cc: e.cc,
        texto: `${e.texto.trim()}${firma ? `\n\n${firma.trim()}` : ''}${nombres.length ? `\n\n[Adjuntos: ${nombres.join(', ')}]` : ''}${reenvio && original ? textoReenvio(original) : ''}`,
      }).catch((err: Error) => toast(`La respuesta salió, pero no quedó en la conversación del caso: ${err.message}`, 'error'));
      void qc.invalidateQueries({ queryKey: ['mensajes-caso', casoId] });
    }
    toast(reenvio ? 'Correo reenviado.' : 'Respuesta enviada.');
    setSel({ base: null, modo: 'responder' });
    // El mensaje enviado aparece en el hilo cuando el buzon se sincroniza (1 a 5 minutos).
    setTimeout(() => void qc.invalidateQueries({ queryKey: ['correos'] }), 4000);
  };

  return (
    <div className="ch">
      <header className="ch-h">
        <button className="btn sm ghost solo-movil-b" onClick={onVolver} aria-label="Volver a la lista">
          <Icon name="chevLeft" />
        </button>
        <div style={{ minWidth: 0, flex: 1 }}>
          <h2>{asunto0}</h2>
          <p className="sub">
            {cuantos} {cuantos === 1 ? 'mensaje' : 'mensajes'} · con {nombreDe(otro)}
            {persona?.personId ? (
              <>
                {' · '}
                <Link to={`/personas/${persona.personId}`}>Ver ficha</Link>
              </>
            ) : null}
          </p>
        </div>
        <div className="ch-acc">
          {enCaso ? null : <CasoDeCorreo hiloId={id} asunto={asunto0} correoCliente={otro?.handle ?? ''} personaId={persona?.personId ?? null} />}
          <button className={`btn sm ${estado?.destacado ? 'on' : 'ghost'}`} onClick={() => cambiar(id, { destacado: !estado?.destacado })} aria-pressed={!!estado?.destacado} title="Destacar">
            <Icon name="star" /> <span className="lbl-m">{estado?.destacado ? 'Destacado' : 'Destacar'}</span>
          </button>
          <button className="btn sm ghost" onClick={() => (cambiar(id, { leidoEn: null }), onVolver())} title="Marcar como no leído">
            <Icon name="mail" /> <span className="lbl-m">No leído</span>
          </button>
          <button
            className="btn sm ghost"
            disabled={historial && !!estado?.archivado}
            onClick={() => {
              void cambiar(id, { archivado: !estado?.archivado });
              toast(estado?.archivado ? 'Vuelve a la entrada.' : 'Archivado. Lo encuentras en Archivados.');
              onVolver();
            }}
            title={historial && estado?.archivado ? 'Historial de Zammad: vuelve a la entrada si se reabre su caso' : estado?.archivado ? 'Devolver a la entrada' : 'Archivar'}
          >
            <Icon name="archive" /> <span className="lbl-m">{estado?.archivado ? 'A la entrada' : 'Archivar'}</span>
          </button>
        </div>
      </header>
      {enCorreo.coincide ? (
        <AvisoCasoEnCorreo
          info={enCorreo}
          personal={enCorreo.esPersonal(buzon)}
          desdeCaso={!!casoElegido}
          reenvio={esReenvioDeCompanero(ms[0] ? { asunto: ms[0].asunto, de: remitente(ms[0])?.handle ?? '' } : null, mios, enCorreo.equipo)}
          onDesdeCaso={() => (setDesdeCaso(true), setReasunto((n) => n + 1), setSel({ base: sel.base, modo: sel.modo === 'nota' ? 'responder' : sel.modo, abrir: Date.now() }))}
          onMiBuzon={() => (setDesdeCaso(false), setReasunto((n) => n + 1))}
        />
      ) : null}
      <div className="ch-ms">
        <Conversacion mensajes={juntarPorFecha(conv, notasComoMensajes(notas))} mios={mios} elegido={sel.base?.id} onAccion={(m, modo) => setSel({ base: m, modo, abrir: Date.now() })} />
      </div>
      <Respuesta
        key={`hilo:${id}`}
        conversacion={`hilo:${id}`}
        mios={mios}
        sel={eleccion}
        setSel={setSel}
        conNota={!!onNota}
        desde={buzon ? `Desde ${buzon.correo}${firma ? ' · con firma' : ''}${ccoGrupo ? ` · CCO a ${ccoGrupo}` : ''}` : 'Sin buzón para responder'}
        datos={{ 'contacto.nombre': (otro?.displayName ?? '').trim().split(/\s+/)[0], 'contacto.apellido': (otro?.displayName ?? '').trim().split(/\s+/).slice(1).join(' ') }}
        enviar={responder}
        reasunto={reasunto}
        asuntoDe={(base, modo) => {
          const original = base?.asunto || asunto0;
          if (caso) return asuntoDeCaso(original, caso.numero, modo, caso.titulo);
          if (casoElegido) return asuntoDeCaso(original, casoElegido.numero, modo, casoElegido.asunto);
          return modo === 'reenviar' ? asuntoReenvio(original) : asuntoRespuesta(original);
        }}
      />
    </div>
  );
}

// Correos empezados y no enviados (se guardan solos al cerrar Redactar).
function Borradores() {
  const lista = useBorradores();
  const [abierto, setAbierto] = useState(false);
  if (!lista.length) return null;
  return (
    <div className="cl-borradores">
      <button className="linkish" onClick={() => setAbierto(!abierto)} aria-expanded={abierto}>
        <Icon name="edit" size={13} /> Borradores ({lista.length})
      </button>
      {abierto
        ? lista.map((b) => (
            <button key={b.id} className="cl-borrador" onClick={() => abrirDialogo({ tipo: 'correo', borrador: b.id })}>
              <b>{b.para || 'Sin destinatario'}</b>
              <span>{b.asunto || '(sin asunto)'}</span>
              <time>{ago(new Date(b.guardado).toISOString())}</time>
            </button>
          ))
        : null}
    </div>
  );
}
